import { createAsyncThunk } from '@reduxjs/toolkit';
import { formatApiError } from '@/utils';
import { IApiError } from '@/models/apiError.model';
import { ORDERS_SLICE_NAME } from './models';
import {
  IOrdersListResponse,
  IGetOrdersListDTO,
  IOrderDetailsResponse,
  IUpdateOrderDeliveryDTO,
  IUpdateOrderPaymentDTO,
  IUpdateOrderPaymentChannelDTO,
  IUpdateOrderProductsDTO,
  IAddOrderCommentDTO,
  IAddOrderCommentResponse,
  IChangeOrderStatusDTO,
  IGetOrderProductsDTO,
  IGetOrderProductsResponse,
  IGetNovaPoshtaDepartmentsResponse,
  IGetNovaPoshtaDepartmentsDTO,
  IUpdateOrderClientDTO,
  IChangeOrderRegionDTO,
  IChangeOrderManagerDTO,
  IGetNewOrdersCountResponse,
  IGetOrderDetailsParams,
  IRecordAdditionalPaymentDTO,
  IRecordRefundDTO,
  IIssueOrderReturnDTO,
} from '@/models/orders.model';
import { IManagersListResponse } from '@/models/admins.model';
import OrdersAPI from 'src/api/OrdersAPI';
import AdminsAPI from 'src/api/AdminsAPI';

export const getOrdersList = createAsyncThunk<
  IOrdersListResponse,
  IGetOrdersListDTO,
  { serializedErrorType: IApiError }
>(
  `${ORDERS_SLICE_NAME}/getOrdersList`,
  async (data) => {
    const response = await OrdersAPI.getOrdersList(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const getNewOrdersCount = createAsyncThunk<IGetNewOrdersCountResponse, void, { serializedErrorType: IApiError }>(
  `${ORDERS_SLICE_NAME}/getNewOrdersCount`,
  async () => {
    const response = await OrdersAPI.getNewOrdersCount();
    return response.data;
  },
  { serializeError: formatApiError },
);

export const getOrderDetails = createAsyncThunk<
  IOrderDetailsResponse,
  IGetOrderDetailsParams | string,
  { serializedErrorType: IApiError }
>(
  `${ORDERS_SLICE_NAME}/getOrderDetails`,
  async (data) => {
    const params = typeof data === 'string' ? { id: data } : data;
    const response = await OrdersAPI.getOrderDetails(params);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const updateOrderPayment = createAsyncThunk<
  IUpdateOrderPaymentDTO,
  IUpdateOrderPaymentDTO,
  { serializedErrorType: IApiError }
>(
  `${ORDERS_SLICE_NAME}/updateOrderPayment`,
  async (data) => {
    await OrdersAPI.updateOrderPayment(data);
    return data;
  },
  { serializeError: formatApiError },
);

export const updateOrderPaymentChannel = createAsyncThunk<
  IUpdateOrderPaymentChannelDTO,
  IUpdateOrderPaymentChannelDTO,
  { serializedErrorType: IApiError }
>(
  `${ORDERS_SLICE_NAME}/updateOrderPaymentChannel`,
  async (data) => {
    await OrdersAPI.updateOrderPaymentChannel(data);
    return data;
  },
  { serializeError: formatApiError },
);

export const updateOrderDelivery = createAsyncThunk<
  IUpdateOrderDeliveryDTO,
  IUpdateOrderDeliveryDTO,
  { serializedErrorType: IApiError }
>(
  `${ORDERS_SLICE_NAME}/updateOrderDelivery`,
  async (data) => {
    await OrdersAPI.updateOrderDelivery(data);
    return data;
  },
  { serializeError: formatApiError },
);

export const updateOrderClient = createAsyncThunk<
  IUpdateOrderClientDTO,
  { order_id: string; data: IUpdateOrderClientDTO },
  { serializedErrorType: IApiError }
>(
  `${ORDERS_SLICE_NAME}/updateOrderClient`,
  async ({ order_id, data }) => {
    await OrdersAPI.updateOrderClient({ order_id, data });
    return data;
  },
  { serializeError: formatApiError },
);

export const updateOrderProducts = createAsyncThunk<
  IUpdateOrderProductsDTO,
  IUpdateOrderProductsDTO,
  { serializedErrorType: IApiError }
>(
  `${ORDERS_SLICE_NAME}/updateOrderProducts`,
  async (data) => {
    await OrdersAPI.updateOrderProducts(data);
    return data;
  },
  { serializeError: formatApiError },
);

export const addOrderComment = createAsyncThunk<
  IAddOrderCommentResponse & { subOrder_id: string },
  IAddOrderCommentDTO,
  { serializedErrorType: IApiError }
>(
  `${ORDERS_SLICE_NAME}/addOrderComment`,
  async (data) => {
    const response = await OrdersAPI.addOrderComment(data);
    return {
      subOrder_id: data.subOrder_id,
      data: response.data.data,
    };
  },
  { serializeError: formatApiError },
);

export const changeOrderStatus = createAsyncThunk<
  IChangeOrderStatusDTO,
  IChangeOrderStatusDTO,
  { serializedErrorType: IApiError }
>(
  `${ORDERS_SLICE_NAME}/changeOrderStatus`,
  async (data, { dispatch }) => {
    await OrdersAPI.changeOrderStatus(data);
    await dispatch(getNewOrdersCount());
    return data;
  },
  { serializeError: formatApiError },
);

export const changeOrderRegion = createAsyncThunk<
  IChangeOrderRegionDTO,
  IChangeOrderRegionDTO,
  { serializedErrorType: IApiError }
>(
  `${ORDERS_SLICE_NAME}/changeOrderRegion`,
  async (data) => {
    await OrdersAPI.changeOrderRegion(data);
    return data;
  },
  { serializeError: formatApiError },
);

export const getOrderProductsList = createAsyncThunk<
  IGetOrderProductsResponse,
  { region_id: string; dto?: IGetOrderProductsDTO },
  { serializedErrorType: IApiError }
>(
  `${ORDERS_SLICE_NAME}/getOrderProductsList`,
  async (data) => {
    const response = await OrdersAPI.getOrderProductsList(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const getNovaPoshtaDepartments = createAsyncThunk<
  IGetNovaPoshtaDepartmentsResponse,
  IGetNovaPoshtaDepartmentsDTO,
  { serializedErrorType: IApiError }
>(
  `${ORDERS_SLICE_NAME}/getNovaPoshtaDepartments`,
  async (data) => {
    const response = await OrdersAPI.getNovaPoshtaDepartments(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const getManagersList = createAsyncThunk<IManagersListResponse, void, { serializedErrorType: IApiError }>(
  `${ORDERS_SLICE_NAME}/getManagersList`,
  async () => {
    const response = await AdminsAPI.getManagersList();
    return response.data;
  },
  { serializeError: formatApiError },
);

export const changeOrderManager = createAsyncThunk<
  IOrderDetailsResponse,
  IChangeOrderManagerDTO,
  { serializedErrorType: IApiError }
>(
  `${ORDERS_SLICE_NAME}/changeOrderManager`,
  async (data) => {
    const response = await OrdersAPI.changeOrderManager(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const deleteOrder = createAsyncThunk<string, string, { serializedErrorType: IApiError }>(
  `${ORDERS_SLICE_NAME}/deleteOrder`,
  async (orderId) => {
    await OrdersAPI.deleteOrder(orderId);
    return orderId;
  },
  { serializeError: formatApiError },
);

export const recordAdditionalPayment = createAsyncThunk<
  IRecordAdditionalPaymentDTO,
  IRecordAdditionalPaymentDTO,
  { serializedErrorType: IApiError }
>(
  `${ORDERS_SLICE_NAME}/recordAdditionalPayment`,
  async (data) => {
    await OrdersAPI.recordAdditionalPayment(data);
    return data;
  },
  { serializeError: formatApiError },
);

export const recordRefund = createAsyncThunk<IRecordRefundDTO, IRecordRefundDTO, { serializedErrorType: IApiError }>(
  `${ORDERS_SLICE_NAME}/recordRefund`,
  async (data) => {
    await OrdersAPI.recordRefund(data);
    return data;
  },
  { serializeError: formatApiError },
);

export const issueOrderReturn = createAsyncThunk<
  IIssueOrderReturnDTO,
  IIssueOrderReturnDTO,
  { serializedErrorType: IApiError }
>(
  `${ORDERS_SLICE_NAME}/issueOrderReturn`,
  async (data) => {
    await OrdersAPI.issueOrderReturn(data);
    return data;
  },
  { serializeError: formatApiError },
);
